import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type } from "arktype";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/auth-card";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Link } from "@/components/ui/link";
import { useAppForm } from "@/hooks/useCreateForm";
import { authClient } from "@/lib/authClient";
import { env } from "../../../env";

const emailForm = type({
  email: "string.email",
});

const searchParams = type({
  error: "string?",
});

export const Route = createFileRoute("/_unauthed/forgot-password")({
  component: RouteComponent,
  validateSearch: searchParams,
});

function RouteComponent() {
  const params = Route.useSearch();
  const navigate = Route.useNavigate();
  if (params.error) {
    toast.error(params.error);
    navigate({
      search: {
        error: undefined,
      },
    });
  }

  const { mutate, data, isPending } = useMutation({
    mutationFn: ({ email }: { email: string }) =>
      authClient.requestPasswordReset({
        email,
        redirectTo: `${env.VITE_CLIENT_BASE_URL}/reset-password`,
      }),
  });

  const form = useAppForm({
    defaultValues: {
      email: "",
    },
    onSubmit: async ({ value: { email } }) => {
      mutate({ email });
    },
    validators: {
      onSubmit: emailForm,
    },
  });

  if (data?.data?.status) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <AuthCard
          description="An email with instructions to reset your password was sent to your account"
          title="Check your email"
        >
          <div className="flex justify-end">
            <Link to="/login" variant="default">
              Back to login
            </Link>
          </div>
        </AuthCard>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <AuthCard
          description="Enter account email. If an account with that email exists, we will send a password reset link."
          title="Forgot your password?"
        >
          <Form>
            <form.AppField
              children={(field) => {
                return (
                  <field.TextField
                    label="Email"
                    placeholder="example@gmail.com"
                    required={false}
                  />
                );
              }}
              name="email"
            />

            <Button
              className="w-full"
              disabled={isPending}
              type="submit"
              variant="auth-primary"
            >
              {isPending && <Loader2 className="mr-2 animate-spin" />}
              Send reset link
            </Button>

            <div className="mt-4 flex justify-end">
              <Link to="/login" variant="auth">
                Back to login
              </Link>
            </div>
          </Form>
        </AuthCard>
      </form>
    </div>
  );
}
